import {
    refreshCalculationJs,
    applyOrderLevelOfferJs,
    applyItemLevelOfferJs,
    applyCouponCodeOfferJs,
    isOfferValidOnCartJs,
} from "@cyntra/calculation-engine";
import { store } from "../index";

const getIsTaxIncluded = () => {
    const gc = store.getState().sessionReducer?.store_config?.general_config || {};
    return gc.item_price_contains_tax === "Include";
};

const toJson = (v) => (typeof v === "string" ? v : JSON.stringify(v));

// Refresh totals on the order (tax, per-item sub_total, grand total, round-off).
// Synchronous — returns the recalculated order object (or null on failure).
// Pass resetDiscount=false to preserve existing discount state.
export const refreshCalculation = (order, opts = {}) => {
    try {
        const {
            resetDiscount = true,
            cashDiscount = 0,
            surcharge = 0,
            selectedPaymentType = "",
            isRoundOffEnabled = false,
            roundOffType = "",
            applicableRoundOffPaymentTypes = [],
            isRefundingOrder = false,
        } = opts;
        const json = refreshCalculationJs(
            toJson(order),
            resetDiscount,
            cashDiscount,
            surcharge,
            selectedPaymentType,
            isRoundOffEnabled,
            roundOffType,
            applicableRoundOffPaymentTypes,
            isRefundingOrder,
            getIsTaxIncluded()
        );
        return JSON.parse(json);
    } catch (e) {
        console.error("[discount_engine] refreshCalculation error", e);
        return null;
    }
};

export const isOfferValidOnCart = async (order, offer) => {
    try {
        const isValid = await isOfferValidOnCartJs(toJson(order), toJson(offer));
        return isValid;
    } catch (e) {
        console.error("[discount_engine] isOfferValidOnCart error", e);
        return false;
    }
};

export const applyOrderLevelOffer = async (order, offer) => {
    // Debug: log what the engine will iterate over for validity/amount check
    try {
        const orderObj = typeof order === "string" ? JSON.parse(order) : order;
        const offerObj = typeof offer === "string" ? JSON.parse(offer) : offer;
        console.log("[debug applyOrderLevelOffer] offer_id", offerObj?.offer_id, "term", offerObj?.offer_term?.[0] && {
            condition_type: offerObj.offer_term[0].condition_type,
            amount: offerObj.offer_term[0].amount,
            count: offerObj.offer_term[0].count,
            offering_type: offerObj.offer_term[0].offering_type,
            is_include_items: offerObj.offer_term[0].is_include_items,
        });
        const engineItems = (orderObj?.item_details || []).map(i => ({
            sku: i.sku,
            name: i.item_name,
            qty: i.quantity,
            sub_total: i.sub_total,
            main_item_id: i.main_item_id,
            isMembership: i.isMembership,
            category_id: i.category_id,
        }));
        const filtered = engineItems.filter(i => (i.main_item_id == null || i.main_item_id === "") && i.isMembership !== true);
        const enginePriceSum = filtered.reduce((a, i) => a + Number(i.sub_total || 0), 0);
        const engineCountSum = filtered.reduce((a, i) => a + Number(i.qty || 0), 0);
        console.log("[debug applyOrderLevelOffer] engine items", engineItems);
        console.log("[debug applyOrderLevelOffer] engine-included (main_item_id empty & !membership)", filtered);
        console.log("[debug applyOrderLevelOffer] engine computed itemPrice=", enginePriceSum, "itemCount=", engineCountSum);
    } catch (e) { /* ignore */ }

    // Validity check independently — narrows down whether isOfferValidOnCart is the gate
    try {
        const validity = await isOfferValidOnCartJs(toJson(order), toJson(offer));
        console.log("[debug applyOrderLevelOffer] isOfferValidOnCart ->", validity);
    } catch (e) {
        console.log("[debug applyOrderLevelOffer] isOfferValidOnCart threw", e);
    }

    // Dump raw JSON strings the engine actually deserializes
    try {
        const offerStr = toJson(offer);
        const orderStr = toJson(order);
        const offerParsed = JSON.parse(offerStr);
        const orderParsed = JSON.parse(orderStr);
        console.log("[debug raw] offer.offer_term[0]", offerParsed?.offer_term?.[0]);
        console.log("[debug raw] offer.offer_term_product", offerParsed?.offer_term_product);
        console.log("[debug raw] offer.offer_header.term_style", offerParsed?.offer_header?.term_style);
        console.log("[debug raw] order.sub_total", orderParsed?.sub_total, "item count", orderParsed?.item_details?.length);
        console.log("[debug raw] order.item_details brief", (orderParsed?.item_details || []).map(i => ({ sku: i.sku, sub_total: i.sub_total, quantity: i.quantity, category_id: i.category_id, main_item_id: i.main_item_id, isMembership: i.isMembership })));
    } catch (e) { /* ignore */ }

    return new Promise((resolve) => {
        try {
            applyOrderLevelOfferJs(
                toJson(offer),
                toJson(order),
                getIsTaxIncluded(),
                (resJson, applied) => {
                    console.log("[debug applyOrderLevelOffer] callback applied=", applied);
                    resolve({ order: resJson ? JSON.parse(resJson) : null, applied });
                }
            );
        } catch (e) {
            console.error("[discount_engine] applyOrderLevelOffer error", e);
            resolve({ order: null, applied: false });
        }
    });
};

export const applyItemLevelOffers = async (order, offers) => {
    return new Promise((resolve) => {
        try {
            applyItemLevelOfferJs(
                toJson(offers),
                toJson(order),
                getIsTaxIncluded(),
                (resJson) => {
                    resolve({ order: resJson ? JSON.parse(resJson) : null });
                }
            );
        } catch (e) {
            console.error("[discount_engine] applyItemLevelOffers error", e);
            resolve({ order: null });
        }
    });
};

export const applyCouponCode = async (order, offers, couponCode) => {
    return new Promise((resolve) => {
        try {
            applyCouponCodeOfferJs(
                toJson(order),
                toJson(offers),
                getIsTaxIncluded(),
                couponCode,
                (resJson, applied) => {
                    resolve({ order: resJson ? JSON.parse(resJson) : null, applied, matched: applied });
                }
            );
        } catch (e) {
            console.error("[discount_engine] applyCouponCode error", e);
            resolve({ matched: false, applied: false, order: null });
        }
    });
};
